<template>
  <view-wrapper
    :prefix="prefix"
    name="Article"
    path="articles"
    :show-cancel="false"
    :save-func="saveArticle"
    :loading="loading"
    :loading-text="loadingText"
    :edit-mode="editing"
    :back-text="'Back to article list'"
  >
    <wrapper class="mb-5" :check-for-child-focused="true">
      <g-input
        type="text"
        label-placeholder="Article name"
        v-model="article.name"
        :editable="true"
      />
      <g-input
        type="text"
        label-placeholder="External identifier"
        v-model="article.externalIdentifier"
        :editable="true"
      />
    </wrapper>

    <g-button
      v-if="editing"
      class="mt-2 mb-6 big primary white"
      @click="gotoToArticleWithPresets()"
      >Duplicate article</g-button
    >

    <wrapper class="mb-5" :check-for-child-focused="true">
      <p>Article data</p>
      <div class="article-data">
        <div
          v-for="(data, index) in article.articleData"
          :key="`article-data--${index}`"
          class="grid grid-cols-5 gap-4"
        >
          <g-input
            type="text"
            :error="data.errors"
            label-placeholder="Key"
            @input="validateUniqueKeys($event, data)"
            v-model="data.key"
            :value="data.key"
            class="col-span-1"
            :editable="true"
          />
          <div class="flex col-span-4 gap-4">
            <g-input
              type="text"
              label-placeholder="Data"
              v-model="data.data"
              :value="data.data"
              :editable="true"
              :is-expandable="true"
            />
            <span
              class="self-center text-xs italic cursor-pointer remove hover:underline"
              @click="removeArticleData(index)"
              >Remove</span
            >
          </div>
        </div>
      </div>
      <g-button class="mt-6 primary" @click="addArticleData">Add data</g-button>
    </wrapper>

    <wrapper class="mb-5">
      <assign-groups
        class="mt-5"
        :groups="availableGroups"
        :presetGroups="presetGroups"
        v-model="article.groups"
        :key="componentKey"
      >
        <template slot="assignedParagraph">
          Assign groups to this article
        </template>
      </assign-groups>
    </wrapper>
  </view-wrapper>
</template>
<script>
import ViewWrapper from "../../components/ViewWrapper";
import Wrapper from "../../components/Wrapper";
import GInput from "../../components/forms/Input";
import GButton from "../../components/forms/Button";
import AssignGroups from "../../components/datacomponents/AssignGroups";

export default {
  components: {
    ViewWrapper,
    Wrapper,
    GInput,
    GButton,
    AssignGroups,
  },
  data: () => ({
    article: {
      name: "",
      articleData: [],
      labels: [],
      assignedLabels: [],
      groups: [],
    },
    presetGroups: [],
    loading: false,
    loadingText: "Getting article",
    articleSaved: false,
    componentKey: 0,
  }),
  computed: {
    editing() {
      return this.$route.params.articleId !== "new";
    },
    prefix() {
      return this.editing ? "Edit" : "Add";
    },
    availableGroups() {
      return this.$store.getters["GROUPS/GROUPS"];
    },
  },
  methods: {
    addArticleData() {
      this.article.articleData.push({ key: "", data: "" });
    },
    removeArticleData(index) {
      this.article.articleData.splice(index, 1);
    },
    validateUniqueKeys(event, data) {
      if (event === "") {
        return;
      }
      const existing = this.article.articleData.filter(ad => ad.key === event);
      if (existing.length > 1) {
        data.errors = "Duplicate key value";
      } else {
        data.errors = "";
      }
    },
    async getArticle() {
      this.loadingText = "Getting article";
      this.loading = true;
      const response = await this.$store.$api.get(
        `/api/articles/${this.$route.params.articleId}`
      );
      this.article = { ...response.data.data };
      this.article.groups = this.article.groups.map(g => ({
        ...g,
        checked: true,
      }));
      this.loading = false;
    },
    async loadPresets() {
      this.loadingText = "Loading presets";
      this.loading = true;

      this.article.name = this.$route.query["name"] || "";

      var articleDataKeys = this.queryParamAsArray("articleData");
      if (articleDataKeys) {
        this.article.articleData = articleDataKeys.map(k => ({
          key: k.indexOf("\n") > 0 ? k.substring(0, k.indexOf("\n")) : k,
          data: k.indexOf("\n") > 0 ? k.substring(k.indexOf("\n") + 1) : "",
        }));
      }

      var articleGroups = this.queryParamAsArray("articleGroups");
      if (articleGroups) {
        const childGroups = this.availableGroups.flatMap(g => g.children);
        this.article.groups = this.availableGroups
          .concat(childGroups)
          .filter(g => articleGroups.includes(g.id + ""));
      }

      this.loading = false;
    },
    queryParamAsArray(name) {
      return typeof this.$route.query[name] == "string"
        ? [this.$route.query[name]]
        : this.$route.query[name] || null;
    },
    async saveArticle() {
      this.loadingText = "Saving article";
      const payload = {
        ...this.article,
        groups: this.article.groups.map(g => {
          return g.id;
        }),
      };
      this.loading = true;
      if (this.editing) {
        payload.articleId = this.article.id;
        await this.$store.$api.put(`/api/articles`, payload);
        this.loading = false;
      } else {
        await this.$store.$api.post("/api/articles", payload);
        this.loading = false;
      }
    },
    gotoToArticleWithPresets() {
      const presetData = {
        name: `${this.article.name} (Copy)`,
        articleGroups: this.article.groups.map(g => g.id),
        articleData: this.article.articleData.map(a => a.key + "\n" + a.data),
      };

      this.article.name = presetData.name;
      this.article.externalIdentifier = null;
      this.$router.push({ path: "/articles/new", query: presetData });
      this.componentKey++;
    },
  },
  async mounted() {
    await this.$store.dispatch("GROUPS/GET_AVAILABLE_GROUPS");

    if (this.editing) {
      await this.getArticle();
    } else {
      this.loadPresets();
    }
    this.componentKey++;
  },
};
</script>
